import React from "react";
import {
  Skeleton,
  Typography,
  Button,
  Row,
  Col,
  Form,
  Input,
  InputNumber,
  Switch,
  Select,
  Image,
  Upload,
  Divider,
  message,
} from "antd";
import { LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import { FormComponent, BaseFormComponentProps } from "../../BaseComponent";
import Config from "../../../config";
import TableTransfer from "../../TableTransfer";
import IShelterProduct from "../../../interfaces/IShelterProduct";
import IShelter from "../../../interfaces/IShelter";
import ISpecie from "../../../interfaces/ISpecie";
import {
  SearchRequest,
  ShelterProductSaveRequest,
} from "../../../services/api/interfaces/ApiRequest";

type ShelterProductProps = BaseFormComponentProps & {
  uuid?: string;
  data?: IShelterProduct | null;
  shelterUuid: string;
  error: any | null;

  shelter: IShelter | null;
  species: ISpecie[] | null;

  loading: boolean;
  contentLoading: boolean;
  history: any;

  loadProduct: (args: SearchRequest) => void;
  loadSpecies: (args: SearchRequest) => void;

  dispatchCreate: (args: ShelterProductSaveRequest) => void;
  dispatchUpdate: (args: ShelterProductSaveRequest) => void;
};

export default class ShelterProduct extends FormComponent<ShelterProductProps> {
  static defaultProps = {
    contentLoading: true,
  };

  state = {
    saving: false,
    loaded: false,
    pictureLoading: false,
    pictureBase64: "",
    branchesSelected: [] as string[],
    speciesSelected: [] as string[],
    branchesSelection: {
      sourceSelectedKeys: [] as any,
      targetSelectedKeys: [] as any,
    },
    speciesSelection: {
      sourceSelectedKeys: [] as any,
      targetSelectedKeys: [] as any,
    },
  };

  // Transfer branches columns
  transferBranchesColumns = {
    left: [
      {
        dataIndex: "name",
        title: "Name",
        searchable: true,
      },
    ],
    right: [
      {
        dataIndex: "name",
        title: "Name",
      },
      {
        dataIndex: "price",
        title: "Price",
        render: (text: any, record: any) => {
          return (
            <div style={{ width: 120 }}>
              <Form.Item
                name={["branches", record.key, "price"]}
                rules={[
                  {
                    required: true,
                    message: "Missing price",
                  },
                ]}
              >
                <InputNumber
                  placeholder="Price by location"
                  min={0}
                  formatter={(value) =>
                    `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value: any) => value.replace(/\$\s?|(,*)/g, "")}
                />
              </Form.Item>
            </div>
          );
        },
      },
      {
        dataIndex: "active",
        title: "Active",
        render: (text: any, record: any) => (
          <div>
            <Form.Item
              name={["branches", record.key, "active"]}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </div>
        ),
      },
      {
        dataIndex: "required",
        title: "Required",
        render: (text: any, record: any) => (
          <div>
            <Form.Item
              name={["branches", record.key, "required"]}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </div>
        ),
      },
    ],
  };

  // Transfer species columns
  transferSpeciesColumns = {
    left: [
      {
        dataIndex: "name",
        title: "Name",
        searchable: true,
      },
    ],
    right: [
      {
        dataIndex: "name",
        title: "Name",
      },
      {
        dataIndex: "required",
        title: "Required",
        render: (text: any, record: any) => (
          <div>
            <Form.Item
              name={["species", record.key, "required"]}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </div>
        ),
      },
    ],
  };

  componentDidMount() {
    const { uuid, loadSpecies, loadProduct } = this.props;

    // Check permissions
    this.validateAccessPermissions();

    // Load Species
    loadSpecies({
      options: {
        limit:9999
      },
    });

    // Load product if we are editing
    if (uuid) {
      loadProduct({
        search: {
          uuid,
          product_branch: {
            with: {
              deleted: false,
              branch: {
                with: {
                  _exists: true,
                  deleted: false,
                },
              },
            },
          },
          product_specie: {
            with: {
              deleted: false,
              specie: {
                with: true,
              },
            },
          },
        },
      });
    }
  }

  componentDidUpdate(prevProps: ShelterProductProps, prevState) {
    const wasLoading = prevProps.loading && !this.props.loading;
    let newState = Object.assign({}, this.state);

    // Check permissions
    this.validateAccessPermissions();

    // We are loaded
    if (
      !this.state.loaded &&
      !this.props.contentLoading &&
      (wasLoading || !this.props.uuid)
    ) {
      newState.loaded = true;

      // Set selected branches and species
      if (this.props.uuid && this.props.data) {
        const { product_branch, product_specie } = this.props.data;

        product_branch?.forEach((p) => {
          if (!newState.branchesSelected.includes(p.branch.uuid)) {
            newState.branchesSelected.push(p.branch.uuid);
          }
        });

        product_specie?.forEach((p) => {
          if (!newState.speciesSelected.includes(p.specie.uuid)) {
            newState.speciesSelected.push(p.specie.uuid);
          }
        });
      }
    }

    // We are saving
    if (this.state.saving && wasLoading) {
      // Add the new role name to url
      if (!this.props.uuid && this.props.data) {
        this.props.history.replace(
          `/organization/shelter/${this.props.shelterUuid}/product/${this.props.data.uuid}`
        );
      }

      if (this.props.error) {
        message.error(`Failed to save product. Reason: ${this.props.error}`);
      } else {
        message.success("Product saved successfully!");
      }

      newState.saving = false;
    }

    // Update if changed
    if (
      newState.loaded !== this.state.loaded ||
      newState.saving !== prevState.saving
    ) {
      this.setState(newState);
    }
  }

  handleBranchSelection = (targetKeys: any, direction: any, moveKeys: any) => {
    this.setState({
      branchesSelected: targetKeys,
    });
  };

  handleSpecieSelection = (targetKeys: any, direction: any, moveKeys: any) => {
    this.setState({
      speciesSelected: targetKeys,
    });
  };

  handleSelectionChange = (
    transferName: string,
    sourceSelectedKeys: string[],
    targetSelectedKeys: string[]
  ) => {
    this.setState((state) => ({
      ...state,
      [transferName]: {
        sourceSelectedKeys,
        targetSelectedKeys,
      },
    }));
  };

  handlePictureUpload = (file: any, fileList: any) => {
    // Parse file to base64
    if (file && file.type.includes("image/")) {
      this.setState(
        {
          pictureLoading: true,
        },
        () => {
          const reader = new FileReader();

          reader.onload = (e: any) => {
            this.setState({
              pictureBase64: e.target.result,
            });
          };

          reader.readAsDataURL(file);
        }
      );
    }

    return false;
  };

  handleSave = (values) => {
    const { uuid, shelterUuid, dispatchCreate, dispatchUpdate } = this.props;
    const { pictureBase64, branchesSelected, speciesSelected } = this.state;

    let payload = {
      organization_product: {
        ...values,
        //shelter_uuid: shelterUuid
      },
      organization_product_branch: branchesSelected.map((branchUuid) => {
        return {
          branch_uuid: branchUuid,
          price: values.branches[branchUuid].price || null,
          active: values.branches[branchUuid].active || false,
          required: values.branches[branchUuid].required || false,
        };
      }) as any,
      organization_product_specie: speciesSelected.map((specieUuid) => {
        return {
          specie_uuid: specieUuid,
          required: values.species[specieUuid]?.required || false,
        };
      }) as any,
    };

    // Set picture if has been changed
    if (pictureBase64.length > 0) {
      payload.organization_product["picture_base64"] = pictureBase64;
    }

    // Remove unnecessary fields
    delete payload.organization_product.branches;
    delete payload.organization_product.species;

    if (!uuid) {
      payload.organization_product["shelter_uuid"] = shelterUuid;

      dispatchCreate(payload);
      this.setState({
        saving: true,
      });
    } else {
      payload = {
        ...payload,
        organization_product: {
          ...payload.organization_product,
          uuid,
        },
      };

      dispatchUpdate(payload);
      this.setState({
        saving: true,
      });
    }
  };

  _renderUploadButton = () => {
    const { pictureLoading } = this.state;

    return (
      <div>
        {pictureLoading ? <LoadingOutlined /> : <PlusOutlined />}
        <div style={{ marginTop: 8 }}>Upload</div>
      </div>
    );
  };

  render() {
    const { uuid, shelter, species, data, loading } = this.props;
    const { loaded, pictureBase64, branchesSelected, speciesSelected } =
      this.state;

    if (!loaded || !shelter) {
      return <Skeleton />;
    }

    let productBranches = {} as any;
    const branchOptions =
      shelter?.organization_branches?.map((branch) => {
        const selected = branchesSelected.includes(branch.uuid);
        const productBranch = data?.product_branch?.find(
          (x) => x.branch.uuid === branch.uuid
        );

        if (selected && productBranch) {
          productBranches[branch.uuid] = productBranch;
        }

        return {
          key: branch.uuid,
          name: branch.name,
          price: selected && productBranch ? productBranch.price : 0,
          active: selected && productBranch ? productBranch.active : false,
          required: selected && productBranch ? productBranch.required : false,
          chosen: selected !== undefined,
        };
      }) || [];

    let productSpecies = {} as any;
    const specieOptions =
      species?.map((specie) => {
        const selected = speciesSelected.includes(specie.uuid);
        const productSpecie = data?.product_specie?.find(
          (x) => (x.specie.uuid = specie.uuid)
        );

        if (selected && productSpecie) {
          productSpecies[specie.uuid] = productSpecie;
        }

        return {
          key: specie.uuid,
          name: specie.name,
          active: selected && productSpecie ? productSpecie.active : false,
          required: selected && productSpecie ? productSpecie.required : false,
          chosen: selected !== undefined,
        };
      }) || [];

    let values: any = {
      description: "",
    };

    if (uuid) {
      values = {
        // Default values
        required: false,
        active: false,

        ...(data || {}),
        branches: productBranches,
        species: productSpecies,
      };
    } else {
      values = {
        // Default values
        required: false,
        active: false,
      };
    }

    return (
      <Form
        name="organization_product"
        initialValues={values}
        layout="vertical"
        onFinish={this.handleSave}
      >
        <Row gutter={12}>
          <Col xs={24} sm={4} md={4}>
            <Upload
              name="picture"
              listType="picture-card"
              accept="image/*"
              className="product-picture-uploader"
              showUploadList={false}
              beforeUpload={this.handlePictureUpload}
            >
              {(uuid && data?.picture) || pictureBase64.length > 0 ? (
                <Image
                  width={150}
                  preview={false}
                  src={
                    pictureBase64.length > 0
                      ? pictureBase64
                      : `${Config.cdnImagesBase}organization/product/picture/AUTOx150/${data?.picture}`
                  }
                />
              ) : (
                this._renderUploadButton()
              )}
            </Upload>
          </Col>
          <Col xs={24} sm={8} md={8}>
            <Row gutter={12}>
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  label="Product name"
                  name="name"
                  rules={[
                    {
                      required: true,
                      message: "Missing a name for the product",
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  label="Price"
                  name="price"
                  rules={[
                    {
                      required: true,
                      message: "Missing a price for the product",
                    },
                  ]}
                >
                  <InputNumber
                    placeholder="Price"
                    min={0}
                    formatter={(value) =>
                      `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    }
                    parser={(value: any) => value.replace(/\$\s?|(,*)/g, "")}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={12}>
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  label="Type"
                  name="type_key"
                  rules={[
                    {
                      required: true,
                      message: "Missing a type for the product",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select a type"
                    options={[
                      {
                        label: "Bundle of products",
                        value: "BOX",
                      },
                      {
                        label: "Single product",
                        value: "UNIQUE",
                      },
                    ]}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={6} md={6}>
                <Form.Item label="Active" name="active" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Col>

              <Col xs={24} sm={6} md={6}>
                <Form.Item
                  label="Required"
                  name="required"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>
          </Col>

          <Col xs={24} sm={12} md={12}>
            <Form.Item
              label="Description"
              name="description"
              rules={[
                {
                  required: true,
                  message: "Missing a description for the product",
                },
              ]}
            >
              <Input.TextArea rows={4} />
            </Form.Item>
          </Col>
        </Row>

        <Divider />

        <Row>
          <Col xs={24}>
          <Form.Item
              name="branchesSelected"
              rules={[
                {
                  required: true,
                  message: "Please add at least one location before proceeding",
                  type:"array",
                  validator: async () => {
                    if (this.state?.branchesSelected?.length > 0) {
                      return this.state.branchesSelected;
                    }
                    throw new Error("Please add at least one location before proceeding");
                  },
                },
              ]}
              label={<Typography.Title level={5}>Configure product by location</Typography.Title>}
              required
            >
            <TableTransfer
              dataSource={branchOptions}
              targetKeys={branchesSelected}
              listStyle={null}
              leftColumns={this.transferBranchesColumns.left}
              rightColumns={this.transferBranchesColumns.right}
              onChange={this.handleBranchSelection}
              onSelectChange={(
                sourceSelectedKeys: string[],
                targetSelectedKeys: string[]
              ) =>
                this.handleSelectionChange(
                  "branchesSelection",
                  sourceSelectedKeys,
                  targetSelectedKeys
                )
              }
            />
            </Form.Item>
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col xs={24}>
          <Form.Item
              name="speciesSelected"
              rules={[
                {
                  required: true,
                  message: "Please add at least one species before proceeding.",
                  validator: async () => {
                    if (this.state?.speciesSelected?.length > 0) {
                      return this.state.speciesSelected;
                    }
                    throw new Error("Please add at least one species before proceeding.");
                  },
                },
              ]}
              label={<Typography.Title level={5}>Configure product by species</Typography.Title>}
              required
            >
              <TableTransfer
                dataSource={specieOptions}
                targetKeys={speciesSelected}
                listStyle={null}
                leftColumns={this.transferSpeciesColumns.left}
                rightColumns={this.transferSpeciesColumns.right}
                onChange={this.handleSpecieSelection}
                onSelectChange={(
                  sourceSelectedKeys: string[],
                  targetSelectedKeys: string[]
                ) =>
                  this.handleSelectionChange(
                    "speciesSelection",
                    sourceSelectedKeys,
                    targetSelectedKeys
                  )
                }
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item style={{ marginTop: 32 }}>
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            loading={loading}
            ref={(refs) => (this.actionRefs.save = refs)}
            block
          >
            Save
          </Button>
        </Form.Item>
      </Form>
    );
  }
}
